import { createContext, useContext, useEffect, useState } from "react";
import { api, setToken, setActiveCompany } from "../lib/api";
import { useTheme } from "./ThemeContext";
import i18n from "../i18n";
import { setCurrency } from "../i18n/format";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

function applyLocale(data) {
  const lang = data?.user?.language || data?.company_language || localStorage.getItem("pp_lang") || "fr";
  if (i18n.language !== lang) i18n.changeLanguage(lang);
  localStorage.setItem("pp_lang", lang);
  setCurrency(data?.company_currency || "cad");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = checking
  const [meta, setMeta] = useState({});
  const { applyServerTheme } = useTheme();

  const refresh = async () => {
    try {
      const data = await api.me();
      setUser(data.user);
      setMeta({ roleLabel: data.role_label, company: data.company, companyId: data.company_id,
        companyLanguage: data.company_language, companyCurrency: data.company_currency, demo: data.demo });
      applyServerTheme(data.user?.theme_preference);
      applyLocale(data);
    } catch {
      setUser(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("pp_token")) refresh();
    else setUser(false);
  }, []);

  const applySession = async (data) => {
    setToken(data.token);
    setActiveCompany(data.user.company_id);
    setUser(data.user);
    setMeta({ roleLabel: data.role_label, company: data.user.company_id });
    applyServerTheme(data.user?.theme_preference);
    await refresh();
  };

  const login = async (email, password, accountant_code) => {
    const data = await api.login({ email, password, accountant_code });
    if (data.twofa_required) return data; // 2FA requis : aucune session créée
    if (data.accountant_code_required) return data; // Code comptable requis
    await applySession(data);
    return data;
  };

  const ssoLogin = async (ticket) => {
    const data = await api.ssoExchange({ ticket });
    await applySession(data);
    return data;
  };

  const verify2fa = async (challenge_id, code) => {
    const data = await api.verify2fa({ challenge_id, code });
    await applySession(data);
    return data;
  };

  const logout = async () => {
    try { await api.logout(); } catch (e) { console.debug("Déconnexion serveur ignorée", e); }
    setToken(null); setActiveCompany(null);
    localStorage.removeItem("pp_demo");
    setUser(false); setMeta({});
  };

  return (
    <AuthContext.Provider value={{ user, meta, login, ssoLogin, verify2fa, logout, refresh, setMeta }}>
      {children}
    </AuthContext.Provider>
  );
}
